/**
 * Adaptador entre .docx (OOXML) y la estructura de datos de Univer Docs.
 *
 * V2 (2026-04-16): formato fiel en el OOXML.
 *
 * Alcance:
 *   - Lectura:
 *     · Descomprime el ZIP (STORE + DEFLATE via DecompressionStream).
 *     · Parsea `word/document.xml` a párrafos + runs con formato
 *       (`<w:rPr>`: bold, italic, underline, strike, color, fontSize, fontFamily).
 *     · Construye `dataStream` + `textRuns` + `paragraphs` para Univer Docs.
 *     · Si el .docx contiene un snapshot JSON de Iberbox (`iberbox/univer-doc.json`)
 *       Y el `dataStream` coincide con el que acabamos de reconstruir del XML,
 *       preferimos el JSON (más fiel — conserva campos que no mapeamos a OOXML,
 *       p. ej. el `documentStyle`). Si difieren → el .docx se editó FUERA de
 *       Iberbox: usamos el reconstruido.
 *
 *   - Escritura:
 *     · Recorre `body.paragraphs` y los `textRuns` activos de cada párrafo.
 *     · Emite un `<w:r>` por run con su `<w:rPr>` completo.
 *     · Empaqueta con el ZIP STORE propio (sin dependencias externas).
 *     · Embebe el snapshot JSON + el `dataStream` actual en
 *       `iberbox/univer-doc.json` (el "signo" para detectar stale-snapshots).
 *
 * NO cubrimos (todavía):
 *   - Imágenes, tablas, secciones, encabezados/pie.
 *   - Estilos nombrados, listas con numeración.
 *   - Se pierden al ir a Word y volver si el archivo se editó externamente
 *     y nuestro parser no entiende un nodo (lo ignoramos y conservamos texto).
 */

const IBB_PART = 'iberbox/univer-doc.json';
const W_NS = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';

/**
 * DocumentStyle por defecto para Univer Docs — reproduce una hoja Word A4.
 *
 *   documentFlavor = 1 (TRADITIONAL) → Univer renderiza p\u00e1ginas separadas
 *                                       con fondo gris y paginaci\u00f3n visible.
 *   pageSize        = A4 en p\u00edxeles a 96 DPI: 794 \u00d7 1123.
 *   m\u00e1rgenes        = 2,54 cm (96 px) superior/inferior/izq/der \u2192 Word default.
 *   renderConfig    = centrado horizontal; valor 0 significa que Univer
 *                     elige autom\u00e1ticamente la mejor alineaci\u00f3n.
 *
 * Si cargamos un .docx con su propio `<w:sectPr><w:pgSz/><w:pgMar/>`, esos
 * valores tienen prioridad y el `documentStyle` se reemplaza por el que
 * trae el snapshot embebido de Iberbox (si existe).
 */
export const DEFAULT_DOC_STYLE = {
  documentFlavor: 1, // DocumentFlavor.TRADITIONAL
  pageSize: { width: 794, height: 1123 }, // A4 @ 96 DPI
  marginTop: 96,
  marginBottom: 96,
  marginLeft: 96,
  marginRight: 96,
  marginHeader: 30,
  marginFooter: 30,
  renderConfig: { vertexAngleDeg: 0, centerAngleDeg: 0 },
};

// Body v\u00e1lido para un header/footer vac\u00edo. Replica exactamente lo que
// genera internamente `getEmptyHeaderFooterBody()` en el UMD de Univer.
// Usado para pre-crear los segmentos default y evitar el bug de
// `CoreHeaderFooterCommand`: al ejecutar `jsonX.insertOp(["headers", id], body)`
// sobre un `headers: {}` reci\u00e9n creado, la inserci\u00f3n del body falla
// silenciosamente (solo se aplica la del id en documentStyle). Resultado:
// `defaultHeaderId` apunta a un segmento inexistente y al editar o pulsar
// Enter, text-x crashea con "Cannot read properties of undefined (reading 't')".
//
// Soluci\u00f3n: entregar a Univer un snapshot con los segmentos ya creados.
// `checkCreateHeaderFooterType` detecta que `defaultHeaderId` existe, devuelve
// `createType: null` y salta la inserci\u00f3n buggy.
function makeEmptyHeaderFooterBody() {
  return {
    dataStream: '\r\n',
    textRuns: [{ st: 0, ed: 0, ts: { fs: 9 } }],
    customBlocks: [],
    paragraphs: [{
      startIndex: 0,
      paragraphStyle: {
        spaceAbove: { v: 0 },
        lineSpacing: 1.5,
        spaceBelow: { v: 0 },
      },
    }],
    sectionBreaks: [{ startIndex: 1 }],
  };
}

// IDs fijos (6 chars) para el header/footer default. No colisionan con los
// que genera `Tools.generateRandomId(6)` en Univer salvo con probabilidad
// astron\u00f3micamente baja, y mantenerlos fijos nos da reproducibilidad.
const DEFAULT_HEADER_ID = 'ibbHdr';
const DEFAULT_FOOTER_ID = 'ibbFtr';

function buildDefaultHeaderFooterMaps() {
  return {
    headers: {
      [DEFAULT_HEADER_ID]: {
        headerId: DEFAULT_HEADER_ID,
        body: makeEmptyHeaderFooterBody(),
      },
    },
    footers: {
      [DEFAULT_FOOTER_ID]: {
        footerId: DEFAULT_FOOTER_ID,
        body: makeEmptyHeaderFooterBody(),
      },
    },
  };
}

// ------------------------------------------------------------------
// Lectura: descomprimir ZIP STORE/DEFLATE y localizar partes
// ------------------------------------------------------------------

/**
 * Lee un ZIP (STORE + DEFLATE) y devuelve Map<path, Uint8Array>.
 *
 * Implementación basada en el **central directory** (al final del archivo),
 * no en los local file headers. Esto es importante porque Word escribe los
 * .docx con el flag bit 3 ("data descriptor"): en ese caso los local file
 * headers llevan compSize=0 y el tamaño real viene DESPUÉS del contenido.
 * El central directory siempre tiene los tamaños correctos, así que iterar
 * por ahí es la forma fiable.
 *
 * Flujo:
 *   1. Localizar el End-Of-Central-Directory (signature 0x06054b50),
 *      escaneando hacia atrás los últimos ~64 KB.
 *   2. Leer centralOffset + centralSize del EOCD.
 *   3. Recorrer el CD (signature 0x02014b50): para cada entry, coger
 *      filename, localHeaderOffset, compSize, method.
 *   4. Para cada entry, saltar su local header (30 + nameLen + extraLen
 *      del LFH real, ojo NO del CDH) y leer compSize bytes.
 *   5. Descomprimir si method = 8 (DEFLATE).
 */
export async function readZipStore(bytes) {
  if (!(bytes instanceof Uint8Array)) bytes = new Uint8Array(bytes);
  const dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const map = new Map();

  // 1. Buscar EOCD escaneando hacia atrás. Firma = 0x06054b50.
  const maxBack = Math.min(bytes.length, 65557); // 22 EOCD + 65535 comentario
  let eocd = -1;
  for (let i = bytes.length - 22; i >= bytes.length - maxBack && i >= 0; i--) {
    if (dv.getUint32(i, true) === 0x06054b50) { eocd = i; break; }
  }
  if (eocd < 0) return map;
  const cdEntries = dv.getUint16(eocd + 10, true);
  const cdSize = dv.getUint32(eocd + 12, true);
  const cdOffset = dv.getUint32(eocd + 16, true);
  if (cdOffset + cdSize > bytes.length) return map;

  // 2. Recorrer el central directory.
  let p = cdOffset;
  for (let n = 0; n < cdEntries && p + 46 <= bytes.length; n++) {
    if (dv.getUint32(p, true) !== 0x02014b50) break; // firma inválida → paramos
    const method = dv.getUint16(p + 10, true);
    const compSize = dv.getUint32(p + 20, true);
    const nameLen = dv.getUint16(p + 28, true);
    const extraLen = dv.getUint16(p + 30, true);
    const commentLen = dv.getUint16(p + 32, true);
    const localHdrOffset = dv.getUint32(p + 42, true);
    const nameStart = p + 46;
    const name = new TextDecoder().decode(bytes.subarray(nameStart, nameStart + nameLen));

    // 3. Ir al local header para este entry y leer los nameLen/extraLen
    //    REALES del LFH (pueden diferir del CDH en extraLen).
    if (localHdrOffset + 30 <= bytes.length && dv.getUint32(localHdrOffset, true) === 0x04034b50) {
      const lfhNameLen = dv.getUint16(localHdrOffset + 26, true);
      const lfhExtraLen = dv.getUint16(localHdrOffset + 28, true);
      const dataStart = localHdrOffset + 30 + lfhNameLen + lfhExtraLen;
      const dataEnd = dataStart + compSize;
      if (dataEnd <= bytes.length) {
        const raw = bytes.subarray(dataStart, dataEnd);
        let content = null;
        if (method === 0) {
          content = raw;
        } else if (method === 8) {
          try { content = await inflateRaw(raw); } catch (_) { content = null; }
        }
        if (content) map.set(name, content);
      }
    }

    p = nameStart + nameLen + extraLen + commentLen;
  }
  return map;
}

async function inflateRaw(bytes) {
  const ds = new DecompressionStream('deflate-raw');
  const stream = new Response(new Blob([bytes]).stream().pipeThrough(ds));
  const ab = await stream.arrayBuffer();
  return new Uint8Array(ab);
}

// ------------------------------------------------------------------
// Parsing de `word/document.xml` → párrafos + runs con formato
// ------------------------------------------------------------------

/**
 * Parsea un `<w:rPr>` y devuelve el bloque `ts` (text style) de Univer.
 * Retorna null si no hay formato relevante (para no hinchar los textRuns).
 */
function parseRPr(rPr) {
  if (!rPr) return null;
  const ts = {};
  const getAttr = (el, name) => el.getAttributeNS(W_NS, name) || el.getAttribute('w:' + name) || el.getAttribute(name);
  const firstChild = (name) => rPr.getElementsByTagNameNS(W_NS, name)[0];

  // Bold: <w:b/> o <w:b w:val="1|true|on">, ausente = no. <w:b w:val="0|false|off"> = explícitamente off.
  const bEl = firstChild('b');
  if (bEl) {
    const v = getAttr(bEl, 'val');
    if (v === undefined || v === null || /^(1|true|on)$/i.test(v)) ts.bl = 1;
    else if (/^(0|false|off)$/i.test(v)) ts.bl = 0;
  }
  const iEl = firstChild('i');
  if (iEl) {
    const v = getAttr(iEl, 'val');
    if (v === undefined || v === null || /^(1|true|on)$/i.test(v)) ts.it = 1;
    else if (/^(0|false|off)$/i.test(v)) ts.it = 0;
  }
  const uEl = firstChild('u');
  if (uEl) {
    const v = getAttr(uEl, 'val');
    // w:u w:val="single" | "none" | "double" | "thick" ...
    if (v && v !== 'none') ts.ul = { s: 1 };
    else if (v === 'none') ts.ul = { s: 0 };
    else ts.ul = { s: 1 };
  }
  const strikeEl = firstChild('strike');
  if (strikeEl) {
    const v = getAttr(strikeEl, 'val');
    if (v === undefined || v === null || /^(1|true|on)$/i.test(v)) ts.st = { s: 1 };
    else if (/^(0|false|off)$/i.test(v)) ts.st = { s: 0 };
  }
  const colorEl = firstChild('color');
  if (colorEl) {
    const v = getAttr(colorEl, 'val');
    if (v && v.toLowerCase() !== 'auto') ts.cl = { rgb: '#' + v.toLowerCase() };
  }
  const szEl = firstChild('sz');
  if (szEl) {
    const v = getAttr(szEl, 'val');
    const n = parseFloat(v);
    if (!isNaN(n)) ts.fs = n / 2; // half-points → points
  }
  const rFontsEl = firstChild('rFonts');
  if (rFontsEl) {
    const f = getAttr(rFontsEl, 'ascii') || getAttr(rFontsEl, 'hAnsi') || getAttr(rFontsEl, 'cs');
    if (f) ts.ff = f;
  }
  // Sub / super
  const vertAlignEl = firstChild('vertAlign');
  if (vertAlignEl) {
    const v = getAttr(vertAlignEl, 'val');
    if (v === 'superscript') ts.va = 'superscript';
    else if (v === 'subscript') ts.va = 'subscript';
  }

  return Object.keys(ts).length ? ts : null;
}

/**
 * Procesa un `<w:r>` (run) añadiendo su contenido al `dataStream` y un
 * textRun al array si tiene formato.
 */
function processRun(r, state) {
  const rPr = firstDirectChild(r, 'rPr');
  const ts = parseRPr(rPr);
  const start = state.dataStream.length;
  for (const node of Array.from(r.childNodes)) {
    if (node.nodeType !== 1) continue;
    const localName = node.localName;
    if (localName === 't') {
      state.dataStream += node.textContent || '';
    } else if (localName === 'tab') {
      state.dataStream += '\t';
    } else if (localName === 'br') {
      state.dataStream += '\r';
    }
    // <w:sym>, <w:fldChar>, <w:instrText>, imagenes… se ignoran por ahora
  }
  const end = state.dataStream.length;
  if (end > start && ts) {
    state.textRuns.push({ st: start, ed: end, ts });
  }
}

/** Busca el primer hijo directo con un local-name dado. */
function firstDirectChild(el, localName) {
  for (const c of Array.from(el.childNodes)) {
    if (c.nodeType === 1 && c.localName === localName) return c;
  }
  return null;
}

/**
 * Recorre recursivamente un contenedor emitiendo todos los `<w:r>` que
 * encuentre y marcando el fin de cada `<w:p>` con un separador `\r`.
 *
 * Los `<w:sdt>` / `<w:sdtContent>` / `<w:hyperlink>` / `<w:smartTag>` son
 * "envoltorios" de Word que no aportan semántica de párrafo pero SÍ
 * contienen runs que queremos leer. Los atravesamos recursivamente.
 */
function walkBody(container, state) {
  for (const child of Array.from(container.childNodes)) {
    if (child.nodeType !== 1) continue;
    const name = child.localName;
    if (name === 'p') {
      // Recorremos su interior (runs + wrappers) y cerramos con \r.
      walkBody(child, state);
      state.paragraphs.push({ startIndex: state.dataStream.length });
      state.dataStream += '\r';
    } else if (name === 'r') {
      processRun(child, state);
    } else if (name === 'sdt' || name === 'sdtContent' || name === 'hyperlink' ||
               name === 'smartTag' || name === 'ins' || name === 'customXml' ||
               name === 'bdo' || name === 'dir' || name === 'moveFrom' || name === 'moveTo') {
      // wrappers transparentes
      walkBody(child, state);
    } else if (name === 'tbl') {
      // Tablas: aplastamos a texto (cada celda → párrafo). MVP.
      walkBody(child, state);
    } else if (name === 'tr' || name === 'tc') {
      walkBody(child, state);
    }
    // ignoramos sectPr, pPr, bookmarkStart/End, proofErr, etc.
  }
}

/**
 * Parsea document.xml y devuelve la estructura {dataStream, textRuns, paragraphs}.
 *
 * Robusto ante .docx de Word que usan wrappers tipo `<w:sdt>`, `<w:hyperlink>`
 * o tablas (los atravesamos en vez de buscar solo hijos directos).
 */
function parseDocumentXml(xmlStr) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xmlStr, 'application/xml');

  // Buscamos el `<w:body>`.
  const bodies = doc.getElementsByTagNameNS(W_NS, 'body');
  const body = bodies[0];

  const state = { dataStream: '', textRuns: [], paragraphs: [] };

  if (body) {
    walkBody(body, state);
  }

  // Si el parser caminó pero no encontró párrafos (body malformado),
  // caemos a un escaneo plano: todos los `<w:t>` en un solo párrafo.
  if (state.paragraphs.length === 0) {
    const ts = Array.from(doc.getElementsByTagNameNS(W_NS, 't'));
    for (const t of ts) state.dataStream += t.textContent || '';
    state.paragraphs.push({ startIndex: state.dataStream.length });
    state.dataStream += '\r';
  }

  // Univer exige un \n final en el dataStream
  state.dataStream += '\n';

  return state;
}

// ------------------------------------------------------------------
// Conversión .docx → Univer Doc data
// ------------------------------------------------------------------

/**
 * Convierte bytes de un .docx a la estructura de datos de Univer Docs.
 *
 * Heurística:
 *   1. Parseamos `word/document.xml` → body reconstruido (con formato).
 *   2. Si existe `iberbox/univer-doc.json` y su `body.dataStream` coincide con
 *      el reconstruido → usamos el JSON (conserva `documentStyle`, etc.).
 *   3. Si difieren o no existe → usamos el reconstruido del XML.
 */
export async function docxBytesToUniverDocData(bytes, opts = {}) {
  const { name } = opts;
  const id = 'doc-' + Date.now();

  let entries;
  try { entries = await readZipStore(bytes); } catch (_) { entries = new Map(); }

  // Reconstrucción desde document.xml
  let reconstructed = null;
  const xml = entries.get('word/document.xml');
  if (xml) {
    try {
      reconstructed = parseDocumentXml(new TextDecoder().decode(xml));
    } catch (_) { reconstructed = null; }
  }

  // Snapshot Iberbox
  let ibbSnapshot = null;
  const ibb = entries.get(IBB_PART);
  if (ibb) {
    try {
      const jsonStr = new TextDecoder().decode(ibb);
      const parsed = JSON.parse(jsonStr);
      if (parsed && parsed.body && typeof parsed.body.dataStream === 'string') {
        ibbSnapshot = parsed;
      }
    } catch (_) { ibbSnapshot = null; }
  }

  // Decidir fuente:
  //   - Si hay ibb y su dataStream coincide con el reconstruido → usar ibb.
  //   - Si no hay reconstruido pero sí ibb → usar ibb (caso borde).
  //   - Si solo hay reconstruido → usar reconstruido.
  //   - Si no hay nada → doc vacío.
  //
  // En cualquier caso, si el documentStyle no trae `documentFlavor` (los .docx
  // externos no tendrán nuestro estilo tipo Word), inyectamos DEFAULT_DOC_STYLE
  // para que Univer renderice con paginación A4.
  const ensureDocStyle = (ds) => {
    const base = (ds && typeof ds.documentFlavor === 'number' && ds.documentFlavor !== 0)
      ? ds
      : Object.assign({}, DEFAULT_DOC_STYLE, ds || {});
    // Fijamos IDs de header/footer default. Si el doc ya trae unos de Word
    // (con sus propios headers reales), respetamos esos.
    if (!base.defaultHeaderId) base.defaultHeaderId = DEFAULT_HEADER_ID;
    if (!base.defaultFooterId) base.defaultFooterId = DEFAULT_FOOTER_ID;
    return base;
  };

  // Pre-crea los segmentos default de header/footer si no existen, para que
  // Univer nunca necesite ejecutar `createHeaderFooterAction` al hacer
  // doble-click (ver coment de buildDefaultHeaderFooterMaps).
  const ensureHeaderFooterMaps = (snap) => {
    if (!snap) return snap;
    const defaults = buildDefaultHeaderFooterMaps();
    if (snap.headers == null) snap.headers = {};
    if (snap.footers == null) snap.footers = {};
    const defaultHeaderId = snap.documentStyle && snap.documentStyle.defaultHeaderId;
    const defaultFooterId = snap.documentStyle && snap.documentStyle.defaultFooterId;
    if (defaultHeaderId && !snap.headers[defaultHeaderId]) {
      snap.headers[defaultHeaderId] = {
        headerId: defaultHeaderId,
        body: makeEmptyHeaderFooterBody(),
      };
    }
    if (defaultFooterId && !snap.footers[defaultFooterId]) {
      snap.footers[defaultFooterId] = {
        footerId: defaultFooterId,
        body: makeEmptyHeaderFooterBody(),
      };
    }
    // Si el snapshot no tiene defaultHeaderId/FooterId pero tampoco headers,
    // inyectamos los nuestros.
    if (!defaultHeaderId && Object.keys(snap.headers).length === 0) {
      Object.assign(snap.headers, defaults.headers);
    }
    if (!defaultFooterId && Object.keys(snap.footers).length === 0) {
      Object.assign(snap.footers, defaults.footers);
    }
    return snap;
  };

  if (ibbSnapshot && reconstructed && ibbSnapshot.body.dataStream === reconstructed.dataStream) {
    ibbSnapshot.id = id;
    ibbSnapshot.documentStyle = ensureDocStyle(ibbSnapshot.documentStyle);
    return ensureHeaderFooterMaps(ibbSnapshot);
  }
  if (ibbSnapshot && !reconstructed) {
    ibbSnapshot.id = id;
    ibbSnapshot.documentStyle = ensureDocStyle(ibbSnapshot.documentStyle);
    return ensureHeaderFooterMaps(ibbSnapshot);
  }
  if (reconstructed) {
    return ensureHeaderFooterMaps({
      id,
      documentStyle: ensureDocStyle(ibbSnapshot && ibbSnapshot.documentStyle),
      body: reconstructed,
      resources: [],
    });
  }
  return createEmptyUniverDocData({ id, name });
}

// ------------------------------------------------------------------
// Helpers de creación
// ------------------------------------------------------------------

export function paragraphsToUniverDoc(pars, opts = {}) {
  const id = opts.id || ('doc-' + Date.now());
  let dataStream = '';
  const paragraphs = [];
  for (const p of pars) {
    dataStream += p;
    paragraphs.push({ startIndex: dataStream.length });
    dataStream += '\r';
  }
  dataStream += '\n';
  // Pre-creamos header/footer default: evita el bug de
  // `CoreHeaderFooterCommand` cuando Univer intenta crear el segmento al
  // hacer doble-click. Ver buildDefaultHeaderFooterMaps en este archivo.
  const hfMaps = buildDefaultHeaderFooterMaps();
  const documentStyle = Object.assign({}, DEFAULT_DOC_STYLE, {
    defaultHeaderId: DEFAULT_HEADER_ID,
    defaultFooterId: DEFAULT_FOOTER_ID,
  });
  return {
    id,
    documentStyle,
    body: { dataStream, textRuns: [], paragraphs },
    headers: hfMaps.headers,
    footers: hfMaps.footers,
    resources: [],
  };
}

export function createEmptyUniverDocData(opts = {}) {
  return paragraphsToUniverDoc([''], opts);
}

// ------------------------------------------------------------------
// Conversión Univer Doc data → .docx bytes
// ------------------------------------------------------------------

/**
 * Recorre los textRuns y devuelve, para cada posición del dataStream, el
 * índice del run activo (o -1 si no hay).
 * Esto simplifica agrupar caracteres consecutivos con el mismo estilo.
 */
function buildRunIndexMap(dataStream, textRuns) {
  const map = new Int16Array(dataStream.length);
  map.fill(-1);
  for (let i = 0; i < textRuns.length; i++) {
    const r = textRuns[i];
    const st = Math.max(0, r.st | 0);
    const ed = Math.min(dataStream.length, r.ed | 0);
    for (let j = st; j < ed; j++) map[j] = i;
  }
  return map;
}

/**
 * Serializa un bloque de `ts` a `<w:rPr>...</w:rPr>` OOXML.
 */
function rPrFromTs(ts) {
  if (!ts) return '';
  const parts = [];
  if (ts.bl) parts.push('<w:b/><w:bCs/>');
  if (ts.it) parts.push('<w:i/><w:iCs/>');
  if (ts.ul && (ts.ul.s === undefined || ts.ul.s)) parts.push('<w:u w:val="single"/>');
  if (ts.st && (ts.st.s === undefined || ts.st.s)) parts.push('<w:strike/>');
  if (ts.cl && typeof ts.cl.rgb === 'string') {
    const rgb = ts.cl.rgb.replace('#', '').toLowerCase();
    if (/^[0-9a-f]{6}$/.test(rgb)) parts.push('<w:color w:val="' + rgb + '"/>');
  }
  if (typeof ts.fs === 'number' && ts.fs > 0) {
    const hp = Math.round(ts.fs * 2);
    parts.push('<w:sz w:val="' + hp + '"/><w:szCs w:val="' + hp + '"/>');
  }
  if (typeof ts.ff === 'string' && ts.ff) {
    const ff = escapeXml(ts.ff);
    parts.push('<w:rFonts w:ascii="' + ff + '" w:hAnsi="' + ff + '" w:cs="' + ff + '"/>');
  }
  if (ts.va === 'superscript') parts.push('<w:vertAlign w:val="superscript"/>');
  else if (ts.va === 'subscript') parts.push('<w:vertAlign w:val="subscript"/>');
  if (!parts.length) return '';
  return '<w:rPr>' + parts.join('') + '</w:rPr>';
}

/**
 * Dado un párrafo (texto entre dos marcadores) y sus runs correspondientes,
 * genera `<w:p>...<w:p>` con los `<w:r>` y formato.
 */
function buildParagraphXml(paraText, runMapSlice, textRuns) {
  if (paraText.length === 0) {
    return '<w:p><w:r><w:t xml:space="preserve"></w:t></w:r></w:p>';
  }
  // Agrupar por run-index consecutivo
  const groups = [];
  let i = 0;
  while (i < paraText.length) {
    const idx = runMapSlice[i];
    let j = i + 1;
    while (j < paraText.length && runMapSlice[j] === idx) j++;
    groups.push({ idx, start: i, end: j });
    i = j;
  }
  const runs = groups.map(g => {
    const text = paraText.slice(g.start, g.end);
    const ts = g.idx >= 0 ? textRuns[g.idx].ts : null;
    const rPr = rPrFromTs(ts);
    // Tabs y saltos se podrían gestionar; de momento \t → <w:tab/>
    const segs = [];
    let buf = '';
    for (const ch of text) {
      if (ch === '\t') {
        if (buf) { segs.push('<w:t xml:space="preserve">' + escapeXml(buf) + '</w:t>'); buf = ''; }
        segs.push('<w:tab/>');
      } else {
        buf += ch;
      }
    }
    if (buf) segs.push('<w:t xml:space="preserve">' + escapeXml(buf) + '</w:t>');
    return '<w:r>' + rPr + segs.join('') + '</w:r>';
  });
  return '<w:p>' + runs.join('') + '</w:p>';
}

/**
 * Serializa una estructura de Univer Doc a un .docx válido.
 *
 * Cada párrafo se escribe con sus runs reales (negrita/cursiva/color/…), lo
 * que produce un .docx **fiel** cuando se abre en Word: no solo el texto, sino
 * también el formato. Además embebemos el snapshot Univer para preservar
 * campos que OOXML no representa (p. ej. `documentStyle`).
 */
export function univerDocDataToDocxBytes(univerData) {
  const body = (univerData && univerData.body) || { dataStream: '\n', textRuns: [], paragraphs: [] };
  const dataStream = body.dataStream || '';
  const textRuns = Array.isArray(body.textRuns) ? body.textRuns : [];
  // Quitamos el `\n` final que Univer añade al cuerpo.
  const content = dataStream.endsWith('\n') ? dataStream.slice(0, -1) : dataStream;

  const runMap = buildRunIndexMap(content, textRuns);

  // Dividir por `\r` → párrafos
  let paragraphs = [];
  let cursor = 0;
  for (let i = 0; i < content.length; i++) {
    if (content[i] === '\r') {
      paragraphs.push({ text: content.slice(cursor, i), runSlice: runMap.slice(cursor, i) });
      cursor = i + 1;
    }
  }
  if (cursor <= content.length) {
    paragraphs.push({ text: content.slice(cursor), runSlice: runMap.slice(cursor) });
  }
  if (paragraphs.length === 0) paragraphs = [{ text: '', runSlice: new Int16Array(0) }];

  // Si el último párrafo es vacío y hay otros, Word igualmente espera uno al final.
  const docBody = paragraphs.map(p => buildParagraphXml(p.text, p.runSlice, textRuns)).join('');

  // sectPr mínima al final (tamaño A4 aprox en twips)
  const sectPr =
    '<w:sectPr>' +
    '<w:pgSz w:w="11906" w:h="16838"/>' + // A4 en twips
    '<w:pgMar w:top="1440" w:right="1440" w:bottom="1440" w:left="1440" w:header="720" w:footer="720" w:gutter="0"/>' +
    '</w:sectPr>';

  const files = [
    {
      path: '[Content_Types].xml',
      content:
        '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
        '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">' +
        '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>' +
        '<Default Extension="xml" ContentType="application/xml"/>' +
        '<Default Extension="json" ContentType="application/json"/>' +
        '<Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/>' +
        '</Types>',
    },
    {
      path: '_rels/.rels',
      content:
        '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
        '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
        '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/>' +
        '</Relationships>',
    },
    {
      path: 'word/document.xml',
      content:
        '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
        '<w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">' +
        '<w:body>' + docBody + sectPr + '</w:body>' +
        '</w:document>',
    },
    {
      // Roundtrip payload: el snapshot completo de Univer.
      path: IBB_PART,
      content: JSON.stringify(univerData),
    },
  ];

  return buildZipStore(files);
}

function escapeXml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

// ------------------------------------------------------------------
// Estadísticas de documento (palabras, caracteres, párrafos)
// ------------------------------------------------------------------

/**
 * Calcula palabras / caracteres / párrafos a partir del dataStream de Univer.
 * Los `\r` separan párrafos y el `\n` final cierra el cuerpo.
 */
export function documentStats(univerData) {
  const dataStream = (univerData && univerData.body && univerData.body.dataStream) || '';
  const body = dataStream.endsWith('\n') ? dataStream.slice(0, -1) : dataStream;
  const plain = body.replace(/\r/g, ' ');
  const charsWithSpaces = plain.length;
  const charsNoSpaces = plain.replace(/\s/g, '').length;
  const paragraphs = body.length === 0 ? 0 : body.split('\r').length;
  const words = plain.split(/\s+/).filter(Boolean).length;
  return { words, charsWithSpaces, charsNoSpaces, paragraphs };
}

// ------------------------------------------------------------------
// ZIP STORE writer
// ------------------------------------------------------------------

function buildZipStore(files) {
  const encoder = new TextEncoder();
  const encoded = files.map((f) => ({
    path: f.path,
    nameBytes: encoder.encode(f.path),
    dataBytes: encoder.encode(f.content),
  }));

  const crcTable = (() => {
    const t = new Uint32Array(256);
    for (let n = 0; n < 256; n++) {
      let c = n;
      for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
      t[n] = c;
    }
    return t;
  })();
  const crc32 = (buf) => {
    let c = 0 ^ -1;
    for (let i = 0; i < buf.length; i++) c = (c >>> 8) ^ crcTable[(c ^ buf[i]) & 0xff];
    return (c ^ -1) >>> 0;
  };

  const localParts = [];
  const centralParts = [];
  let offset = 0;

  for (const f of encoded) {
    const crc = crc32(f.dataBytes);
    const size = f.dataBytes.length;
    const nameLen = f.nameBytes.length;

    const lfh = new Uint8Array(30 + nameLen);
    const dv = new DataView(lfh.buffer);
    dv.setUint32(0, 0x04034b50, true);
    dv.setUint16(4, 20, true);
    dv.setUint16(6, 0, true);
    dv.setUint16(8, 0, true);
    dv.setUint16(10, 0, true);
    dv.setUint16(12, 0, true);
    dv.setUint32(14, crc, true);
    dv.setUint32(18, size, true);
    dv.setUint32(22, size, true);
    dv.setUint16(26, nameLen, true);
    dv.setUint16(28, 0, true);
    lfh.set(f.nameBytes, 30);

    localParts.push(lfh, f.dataBytes);
    const localHeaderOffset = offset;
    offset += lfh.length + size;

    const cdh = new Uint8Array(46 + nameLen);
    const dv2 = new DataView(cdh.buffer);
    dv2.setUint32(0, 0x02014b50, true);
    dv2.setUint16(4, 20, true);
    dv2.setUint16(6, 20, true);
    dv2.setUint16(8, 0, true);
    dv2.setUint16(10, 0, true);
    dv2.setUint16(12, 0, true);
    dv2.setUint16(14, 0, true);
    dv2.setUint32(16, crc, true);
    dv2.setUint32(20, size, true);
    dv2.setUint32(24, size, true);
    dv2.setUint16(28, nameLen, true);
    dv2.setUint16(30, 0, true);
    dv2.setUint16(32, 0, true);
    dv2.setUint16(34, 0, true);
    dv2.setUint16(36, 0, true);
    dv2.setUint32(38, 0, true);
    dv2.setUint32(42, localHeaderOffset, true);
    cdh.set(f.nameBytes, 46);
    centralParts.push(cdh);
  }

  const centralSize = centralParts.reduce((a, b) => a + b.length, 0);
  const centralOffset = offset;

  const eocd = new Uint8Array(22);
  const dv3 = new DataView(eocd.buffer);
  dv3.setUint32(0, 0x06054b50, true);
  dv3.setUint16(4, 0, true);
  dv3.setUint16(6, 0, true);
  dv3.setUint16(8, encoded.length, true);
  dv3.setUint16(10, encoded.length, true);
  dv3.setUint32(12, centralSize, true);
  dv3.setUint32(16, centralOffset, true);
  dv3.setUint16(20, 0, true);

  const totalLen = offset + centralSize + eocd.length;
  const out = new Uint8Array(totalLen);
  let p = 0;
  for (const part of localParts) {
    out.set(part, p);
    p += part.length;
  }
  for (const part of centralParts) {
    out.set(part, p);
    p += part.length;
  }
  out.set(eocd, p);
  return out;
}
